// ═══════════════════════════════════════════════════════════════════════
// @sloe/sloedata — the Lab's shared SloeData (D-086) plumbing
// ═══════════════════════════════════════════════════════════════════════
// The Lab database is Neon behind the Neon Data API (PostgREST-compatible, so
// supabase-js works as the query client). The Data API accepts only SloeAuth
// JWTs for audience https://data.sloelabs.com/lab and runs each request as the
// Postgres role in the token's `role` claim.
//
// Used by the tenant server (Node), the Studio's /api functions (Cloudflare
// Worker, nodejs_compat) and the browsers of both apps, so everything here is
// isomorphic: fetch, AbortSignal, atob/btoa — no Buffer, no node:*.
//
//   createBackendTokenSource   the backend's machine token (client_credentials →
//                              role lab_service, BYPASSRLS). Single flight,
//                              cached until 30 s before expiry, bounded by a
//                              timeout, degrades to the still-valid old token,
//                              never puts a secret or a token in an error.
//   retryingFetch              retries HTTP 503 SQLSTATE PT503 — Neon sometimes
//                              delivers the first request on a fresh pooled
//                              connection without its JWT context; the Lab DB
//                              turns that into PT503 instead of a silent empty
//                              result. Copied from Sloe-AI-Network PR #5.
//   rejectedTokenRetryFetch    one retry with a freshly minted backend token when
//                              the Data API answers 401 (the JWT was refused
//                              before the statement ran, so replaying is safe).
//   normalizeDataApiBase       supabase-js appends /rest/v1 itself.
// ═══════════════════════════════════════════════════════════════════════

export const DEFAULT_LAB_DATA_AUDIENCE = "https://data.sloelabs.com/lab";

/** supabase-js appends /rest/v1; accept the Data API URL in either form. */
export function normalizeDataApiBase(raw: string): string {
  return String(raw || "").trim().replace(/\/+$/, "").replace(/\/rest\/v1$/, "");
}

// ───────────────────────────── backend token ─────────────────────────────
export type BackendTokenConfig = {
  issuer: string;          // https://auth.sloelabs.com (origin only)
  clientId: string;
  clientSecret: string;
  resource: string;        // https://data.sloelabs.com/lab
  scope?: string;          // default "sloe:access"
  timeoutMs?: number;      // default 10 000
  refreshSkewMs?: number;  // default 30 000
  fetchImpl?: typeof fetch;
  now?: () => number;
};

export class BackendTokenError extends Error {
  readonly status: number | null;
  readonly oauthError: string | null;
  constructor(message: string, status: number | null = null, oauthError: string | null = null) {
    super(message);
    this.name = "BackendTokenError";
    this.status = status;
    this.oauthError = oauthError;
  }
}

export type BackendTokenSource = {
  /** A token valid for at least `refreshSkewMs` more (or the last still-valid one while the issuer is failing). */
  getToken(): Promise<string>;
  /** Drop the cached token; the next call mints a new one. */
  invalidate(): void;
};

function b64urlDecode(s: string): string {
  const p = s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (s.length % 4)) % 4);
  return atob(p);
}

// Only `exp` is read, and only when the token endpoint omits expires_in.
// Never verified here — the Data API verifies the token.
function jwtExpMs(token: string): number | null {
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const claims = JSON.parse(b64urlDecode(payload));
    return typeof claims?.exp === "number" ? claims.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function createBackendTokenSource(cfg: BackendTokenConfig): BackendTokenSource {
  const fetchImpl = cfg.fetchImpl ?? ((input: any, init?: any) => fetch(input, init));
  const now = cfg.now ?? Date.now;
  const timeoutMs = cfg.timeoutMs ?? 10_000;
  const skewMs = cfg.refreshSkewMs ?? 30_000;
  const endpoint = `${cfg.issuer.replace(/\/+$/, "")}/api/auth/oauth2/token`;

  let cached: { token: string; expMs: number } | null = null;
  let inflight: Promise<string> | null = null;

  async function mint(): Promise<{ token: string; expMs: number }> {
    let res: Response;
    try {
      res = await fetchImpl(endpoint, {
        method: "POST",
        headers: {
          "content-type": "application/x-www-form-urlencoded",
          authorization: `Basic ${btoa(`${cfg.clientId}:${cfg.clientSecret}`)}`,
        },
        body: new URLSearchParams({
          grant_type: "client_credentials",
          scope: cfg.scope ?? "sloe:access",
          resource: cfg.resource,
        }).toString(),
        signal: AbortSignal.timeout(timeoutMs),
      });
    } catch (e: any) {
      const reason = e?.name === "TimeoutError" || e?.name === "AbortError" ? "timed out" : "unreachable";
      throw new BackendTokenError(`sloeauth token endpoint ${reason}`);
    }
    let body: any = null;
    try { body = await res.json(); } catch { body = null; }
    if (!res.ok) {
      const code = typeof body?.error === "string" ? body.error.slice(0, 64) : null;
      throw new BackendTokenError(`sloeauth token endpoint ${res.status}${code ? `: ${code}` : ""}`, res.status, code);
    }
    const token = typeof body?.access_token === "string" ? body.access_token : "";
    if (!token) throw new BackendTokenError("sloeauth token endpoint returned no access_token", res.status);
    const issuedAt = now();
    const expiresIn = Number(body?.expires_in);
    const expMs = Number.isFinite(expiresIn) && expiresIn > 0
      ? issuedAt + expiresIn * 1000
      : (jwtExpMs(token) ?? issuedAt + 300_000);
    return { token, expMs };
  }

  return {
    async getToken(): Promise<string> {
      if (cached && now() < cached.expMs - skewMs) return cached.token;
      if (inflight) return inflight;
      const p = (async () => {
        try {
          cached = await mint();
          return cached.token;
        } catch (err) {
          // Degrade, don't fail: the previous token is still accepted until its
          // own expiry. Keep a 2 s margin so it cannot die on the wire.
          if (cached && now() < cached.expMs - 2_000) return cached.token;
          throw err;
        } finally {
          inflight = null;
        }
      })();
      inflight = p;
      return p;
    },
    invalidate(): void {
      cached = null;
    },
  };
}

// ───────────────────────────── fetch wrappers ─────────────────────────────
const LOST_IDENTITY = "PT503";

/** Retry requests that reached Postgres without their JWT identity (HTTP 503, SQLSTATE PT503). */
export function retryingFetch(base: typeof fetch = (i: any, n?: any) => fetch(i, n), attempts = 4): typeof fetch {
  return (async (input: any, init?: any) => {
    for (let attempt = 1; ; attempt++) {
      const res = await base(input, init);
      if (res.status !== 503 || attempt >= attempts) return res;
      const body = (await res.clone().json().catch(() => null)) as { code?: string } | null;
      if (body?.code !== LOST_IDENTITY) return res;
      // supabase-js sends string bodies, so the same init can be replayed.
      await new Promise((r) => setTimeout(r, 100 * attempt + Math.random() * 100));
    }
  }) as typeof fetch;
}

/**
 * For the BACKEND client only: a 401 means the JWT was refused before the
 * statement ran (expired early through clock skew, JWKS rotation), so one
 * replay with a freshly minted token is safe even for writes. At most one
 * re-mint per `cooldownMs`: a misconfiguration that makes every token fail must
 * not turn each query into a token request against SloeAuth.
 */
export function rejectedTokenRetryFetch(
  source: BackendTokenSource,
  base: typeof fetch = (i: any, n?: any) => fetch(i, n),
  cooldownMs = 10_000,
  onRetry?: () => void,
): typeof fetch {
  let lastRemintAt = 0;
  return (async (input: any, init?: any) => {
    const first = await base(input, init);
    if (first.status !== 401) return first;
    if (Date.now() - lastRemintAt < cooldownMs) return first;
    lastRemintAt = Date.now();
    source.invalidate();
    let fresh: string;
    try { fresh = await source.getToken(); } catch { return first; }
    const headers = new Headers(init?.headers);
    headers.set("Authorization", `Bearer ${fresh}`);
    onRetry?.();
    return base(input, { ...init, headers });
  }) as typeof fetch;
}

// ───────────────────────────── env ─────────────────────────────
export type LabDataEnvConfig = {
  url: string;
  issuer: string;
  clientId: string;
  clientSecret: string;
  audience: string;
};

/** Backend data config from env. null (with the missing names) when incomplete. */
export function labBackendConfigFromEnv(env: Record<string, string | undefined>): { config: LabDataEnvConfig | null; missing: string[] } {
  const url = normalizeDataApiBase(env.LAB_DATA_API_URL || "");
  const issuer = (env.SLOEAUTH_ISSUER || "").trim().replace(/\/+$/, "");
  const clientId = (env.LAB_BACKEND_OIDC_CLIENT_ID || "").trim();
  const clientSecret = (env.LAB_BACKEND_OIDC_CLIENT_SECRET || "").trim();
  const audience = (env.SLOEAUTH_AUDIENCE || DEFAULT_LAB_DATA_AUDIENCE).trim();
  const missing: string[] = [];
  if (!url) missing.push("LAB_DATA_API_URL");
  if (!issuer) missing.push("SLOEAUTH_ISSUER");
  if (!clientId) missing.push("LAB_BACKEND_OIDC_CLIENT_ID");
  if (!clientSecret) missing.push("LAB_BACKEND_OIDC_CLIENT_SECRET");
  return { config: missing.length ? null : { url, issuer, clientId, clientSecret, audience }, missing };
}

// ───────────────────────────── SloeAuth JWT verification ─────────────────────────────
// EdDSA (Ed25519) against SloeAuth's JWKS, via WebCrypto — the same code runs in
// the tenant (Node 22) and the Studio's Cloudflare Worker. Port of the auth-kit's
// verifyJwt, plus: kid-miss refetch, single-flight JWKS fetch, required exp.
type Jwk = { kid?: string; kty: string; crv?: string; x?: string; alg?: string };

export type JwtVerifyOptions = {
  /** REQUIRED: the `aud` the token must carry (one of). A verifier call without it is refused. */
  audience: string | string[];
  typ?: string;
};

export type JwtVerifier = {
  verify(jwt: string, opts: JwtVerifyOptions): Promise<Record<string, unknown>>;
};

function b64urlBytes(s: string): Uint8Array {
  const bin = b64urlDecode(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function createJwtVerifier(cfg: {
  issuer: string;
  fetchImpl?: typeof fetch;
  now?: () => number;
  jwksTtlMs?: number;
  timeoutMs?: number;
}): JwtVerifier {
  const issuer = cfg.issuer.replace(/\/+$/, "");
  const fetchImpl = cfg.fetchImpl ?? ((i: any, n?: any) => fetch(i, n));
  const now = cfg.now ?? Date.now;
  const ttl = cfg.jwksTtlMs ?? 10 * 60_000;
  let cache: { keys: Jwk[]; at: number } | null = null;
  let inflight: Promise<Jwk[]> | null = null;
  const keyCache = new Map<string, any>(); // CryptoKey — typed loosely so the package compiles without the DOM lib

  async function fetchJwks(force = false): Promise<Jwk[]> {
    if (!force && cache && now() - cache.at < ttl) return cache.keys;
    if (inflight) return inflight;
    const p = (async () => {
      try {
        const r = await fetchImpl(`${issuer}/api/auth/jwks`, { signal: AbortSignal.timeout(cfg.timeoutMs ?? 10_000) });
        if (!r.ok) throw new Error(`jwks ${r.status}`);
        const keys = ((await r.json()) as { keys?: Jwk[] }).keys || [];
        cache = { keys, at: now() };
        return keys;
      } finally {
        inflight = null;
      }
    })();
    inflight = p;
    return p;
  }

  async function keyFor(jwk: Jwk): Promise<any> {
    const id = `${jwk.kid ?? ""}:${jwk.x ?? ""}`;
    let k = keyCache.get(id);
    if (!k) {
      k = await crypto.subtle.importKey("jwk", { kty: "OKP", crv: "Ed25519", x: jwk.x } as any, { name: "Ed25519" } as any, false, ["verify"]);
      keyCache.set(id, k);
    }
    return k;
  }

  return {
    async verify(jwt, opts) {
      // Audience is not optional: a token minted for another resource or client
      // must never be accepted just because a caller forgot to say which one.
      const wanted = opts && opts.audience !== undefined
        ? (Array.isArray(opts.audience) ? opts.audience : [opts.audience]).filter((a) => typeof a === "string" && a.length > 0)
        : [];
      if (wanted.length === 0) throw new Error("audience required");
      const [h, p, s] = String(jwt || "").split(".");
      if (!h || !p || !s) throw new Error("malformed jwt");
      const header = JSON.parse(b64urlDecode(h)) as { alg?: string; kid?: string; typ?: string };
      if (header.alg !== "EdDSA") throw new Error(`unexpected alg ${header.alg}`);
      if (opts.typ && header.typ !== opts.typ) throw new Error(`unexpected typ ${header.typ}`);
      let keys = await fetchJwks();
      let jwk = keys.find((k) => k.kid === header.kid) ?? (header.kid ? undefined : keys[0]);
      if (!jwk && header.kid) {
        keys = await fetchJwks(true); // the issuer may have rotated
        jwk = keys.find((k) => k.kid === header.kid);
      }
      if (!jwk || jwk.kty !== "OKP" || jwk.crv !== "Ed25519") throw new Error("no matching Ed25519 key");
      const ok = await crypto.subtle.verify({ name: "Ed25519" } as any, await keyFor(jwk), b64urlBytes(s) as any, new TextEncoder().encode(`${h}.${p}`));
      if (!ok) throw new Error("bad signature");
      const claims = JSON.parse(b64urlDecode(p)) as Record<string, unknown>;
      const nowSec = Math.floor(now() / 1000);
      if (claims.iss !== issuer) throw new Error("issuer mismatch");
      if (typeof claims.exp !== "number" || claims.exp <= nowSec) throw new Error("expired");
      if (typeof claims.nbf === "number" && claims.nbf > nowSec + 30) throw new Error("not yet valid");
      const aud = claims.aud;
      const got = Array.isArray(aud) ? aud.map(String) : typeof aud === "string" ? [aud] : [];
      if (!got.some((a) => wanted.includes(a))) throw new Error("audience mismatch");
      return claims;
    },
  };
}
